import { useState, useCallback, useRef } from 'react';
import { Vector3 } from 'three';
import { IntersectionPoint, detectBeamIntersection } from '@/utilities/BeamIntersectionUtils';
import { useModelStore } from '@/store/useModelStore';
import { structuralElementManager, Beam3D } from '@/utilities/StructuralElementManager';

export const useBeamIntersection = (camera: THREE.Camera, canvas: HTMLCanvasElement) => {
    const [intersectionPoint, setIntersectionPoint] = useState<IntersectionPoint | null>(null);
    const [showDialog, setShowDialog] = useState(false);
    const intersectingBeamRef = useRef<Beam3D | null>(null);

    const store = useModelStore();

    const handleBeamIntersection = useCallback((newBeam: Beam3D) => {
        // Get all existing beams
        const existingBeams = structuralElementManager.getBeams();
        
        // Check for intersections with each existing beam
        for (const existingBeam of existingBeams) {
            // Skip self-intersection
            if (existingBeam.id === newBeam.id) continue;

            // Check for intersection in 3D space
            const intersection = detectBeamIntersection(
                newBeam,
                existingBeam,
                structuralElementManager.getNodesMap()
            );

            if (intersection) {
                // Add screen position for popup
                const worldPos = new Vector3(
                    intersection.point.x,
                    intersection.point.y,
                    intersection.point.z
                );
                
                const screenPos = worldPos.clone().project(camera);
                intersection.screenPosition = {
                    x: Math.round((screenPos.x + 1) * canvas.width / 2),
                    y: Math.round((-screenPos.y + 1) * canvas.height / 2)
                };

                setIntersectionPoint(intersection);
                intersectingBeamRef.current = existingBeam;
                setShowDialog(true);
                return true;
            }
        }

        return false;
    }, [camera, canvas]);

    const handleIntersectionConfirm = useCallback(() => {
        if (!intersectionPoint || !intersectingBeamRef.current) return;

        const result = structuralElementManager.createNodeAtIntersection(intersectionPoint);
        
        if (result.success) {
            // Add the new node
            const intersectionNode = structuralElementManager.getNode(result.nodeId);
            if (intersectionNode) {
                // Create node object that matches store's expected format
                store.addNode({
                    x: intersectionNode.x,
                    y: intersectionNode.y,
                    z: intersectionNode.z
                });

                // Update beams
                result.newBeams.forEach(beam => {
                    store.addBeam?.(
                        beam.startNodeId,
                        beam.endNodeId
                    );
                });

                // Remove the original beam
                if (result.removedBeamId) {
                    store.deleteBeam?.(result.removedBeamId);
                }
            }
        }

        setShowDialog(false);
        setIntersectionPoint(null);
        intersectingBeamRef.current = null;
    }, [intersectionPoint, store]);

    const handleIntersectionCancel = useCallback(() => {
        setShowDialog(false);
        setIntersectionPoint(null);
        intersectingBeamRef.current = null;
    }, []);

    return {
        showDialog,
        intersectionPoint,
        handleBeamIntersection,
        handleIntersectionConfirm,
        handleIntersectionCancel
    };
};
